import jss from "jss";
import jssNested from "jss-nested";
import * as THREE from "three";

import { DeviceOrientationControls } from "three-stdlib";
import { OrbitOrientation } from "./controls/OrbitOrientation";
import { OrbitControls } from "./controls/OrbitControls";
import Scene360 from "./Scene360";
import Scene3D from "./Scene3D";
import SceneOcean from "./SceneOcean";
import SceneOceanFixed from "./SceneOceanFixed";
import SceneIntro from "./SceneIntro";
import SceneManager from "./SceneManager";
import TextureManager from "./TextureManager";
import LoadingManager from "./LoadingManager";
import styles from "./styles";
import { is_touch_device } from "./helpers";

import eventTours from "./IframeControl";
import SceneSplit from "./SceneSplit";

/**
 * The player class displays a 360 scene within the specified container.
 */
const MAX_VERT_VIEW_ANGLE = 135; //Maximum angle the viewer can look up (degrees)
const MIN_VERT_VIEW_ANGLE = 55; // Minimum angle the viewer can look down (degrees)

export default class Player {
  container;

  /**
   * Stylesheet for the player.
   * @type {jss.StyleSheet}
   */
  stylesheet;

  config;
  title;
  camera;
  renderer;
  controlsOrbit;
  controlsDevice;
  sceneManager;
  loadingManager;
  textureManager;
  activeImage;
  _transitioning;
  splashscreen;
  disableHotspots;
  isThumbnail;
  prefetchedScenes;
  /**
   * This is used to prevent clicks on dragging
   */
  mouseDownPosition;
  isPivoting;

  /**
   * Mouse events override for editor
   */
  pauseEvents = false;
  tempCallback;

  slowConnection;
  slowConnectionTimer;

  // WebXR
  supportsVR = false;
  VRSession = null;

  altImage = null;

  CAMERA_FOV = 80;
  THUMBNAIL_CAMERA_FOV = 45;
  PARTIAL_FOV = 60;

  autoplayTransitionTimeout;
  autoplayProgressInterval;
  autoplayProgressStartTime;
  autoplayProgressDuration;
  loopedAutoplay = false;
  isLoadingPOIHotspot = false;
  hasPlayedInitialLookAt = false;
  isInitialTourLoad = true;

  /**
   * Attach player to container
   * @param {Element | null} container
   * @param tour
   * @param mode
   * @param isThumbnail
   * @param {Object} options - Configuration options
   * @param {Function} options.toggleUI - Called to toggle the UI
   * @param {Function} options.setUIVisibility - Used to set the Visibility of the main UI
   * @param {Function} options.getUIVisibility - returns true if visible false if not
   * @param {Function} onStartImageLoad - Called when transition between scenes is started
   * @param {Function} onEndImageLoad - Called when transition between scenes stops
   * @param {Function} options.onAutoPlayStateChange - Called when start and stop auto-play
   * @param {Function} options.onCompletedAutoplay - Called when auto-play completed all images
   */
  constructor(
    container,
    tour,
    mode = "player",
    isThumbnail,
    options = {
      toggleUI: () => { },
      setUIVisibility: () => { },
      getUIVisibility: () => { },
      onAutoPlayStateChange: () => { },
      onCompletedAutoplay: () => { },
      resetProgressBar: () => { },
      onAutoplayProgressUpdate: () => { },
    },
    onStartImageLoad = () => { },
    onEndImageLoad = () => { },
  ) {
    this.mode = mode;
    this.slowConnection = true;
    this.isDisposed = false;
    this.isPivoting = false;
    this.prefetchedScenes = [];

    this.onStartImageLoad = onStartImageLoad;
    this.onEndImageLoad = onEndImageLoad;
    this.isShowingPosterPage = options.splashScreen;

    if (options.onDeviceControlsSupported) {
      this.onDeviceControlsSupported = options.onDeviceControlsSupported;
    }

    if (container === null) {
      throw Error("Container was null");
    }

    this.crosshair = new THREE.Mesh(
      new THREE.RingBufferGeometry(0.02, 0.04, 32),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        opacity: 0.5,
        transparent: true,
      }),
    );

    this.disableHotspots = false;
    this._options = options;

    this.isThumbnail = isThumbnail === undefined ? false : isThumbnail;
    this.params = new URLSearchParams(window.location.search.substring(1));
    this.is3d = this.params.get("3D") === "true" || this.params.get("3d") === "true";

    const fixedOceans = [
      "04-U48X",
      "VQ-A3YL",
      "N4-O81M",
      "54-02LL",
      "VX-K30B",
      "L1-W9D0",
      "M3-W5DK",
      "5O-ZRDK",
      "GA-W5A4",
      "H9-XXHC",
      "GP-WUBP",
      "YV-U3OZ",
      "UX-Q435",
      "U0-AWM8",
      "NY-6B28",
      "UO-69LO",
    ];

    this.is3dOcean = false;

    if (
      fixedOceans.indexOf(tour.token) >= 0 &&
      window.location.href.indexOf("editor") === -1
    ) {
      if (this.is3d) {
        SceneOcean.injectExample(tour);
        this.is3dOcean = true;
      } else {
        // SceneOceanFixed.injectExample(tour);
      }
    }

    if (
      tour.token === "JY-7FBQ" &&
      window.location.href.indexOf("editor") === -1
    ) {
      SceneIntro.injectExample(tour);
    }

    const config = tour.data;
    this.config = config;
    if (this.config.options) {
      if ("fov" in this.config.options) {
        this.CAMERA_FOV = this.config.options.fov;
      }
    }

    // if (this.config.floorplan3D) {
    //   this.has3DFloorplans = this.config.floorplan3D.enabled;
    // }

    this.title = tour.title;

    jss.use(jssNested());
    this.stylesheet = jss.createStyleSheet(styles, { link: true }).attach();

    this.container = container;
    this.container.classList.add(this.stylesheet.classes.container);

    const width = container.clientWidth;
    const height = container.clientHeight;

    // Setup rendering
    const fov = this.isThumbnail ? this.THUMBNAIL_CAMERA_FOV : this.CAMERA_FOV;
    this.camera = new THREE.PerspectiveCamera(fov, width / height, 1, 1100);
    this.camera.layers.enable(1);
    this.camera.position.z = 1;

    if (this._options.renderer) {
      this.renderer = this._options.renderer;
    } else {
      if (this.config.intro !== undefined) {
        const { intro } = this.config;

        if (intro.type === "ocean" && !this.is3d) {
          this.renderer = new THREE.WebGLRenderer({
            alpha: true,
            powerPreference: "high-performance",
          });
          this.renderer.setClearColor(0x000000, 0);
        } else {
          this.renderer = new THREE.WebGLRenderer({
            powerPreference: "high-performance",
          });
        }
      } else {
        this.renderer = new THREE.WebGLRenderer({
          powerPreference: "high-performance",
        });
      }
    }

    const pixelRatio = this.isThumbnail
      ? window.devicePixelRatio * 3
      : window.devicePixelRatio;
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(width, height);
    this.renderer.domElement.style.width = width + "px";
    this.renderer.domElement.style.height = height + "px";
    this.container.appendChild(this.renderer.domElement);
    this.renderer.autoClear = false;
    this.renderer.setScissorTest(true);

    // Setup controls
    this.controlsOrbit = new OrbitControls(
      this.camera,
      this.renderer.domElement,
    );

    // this.controlsOrbit.mainCamera = this.camera;
    // this.controlsOrbit.enableDamping = true;
    // this.controlsOrbit.dampingFactor = 0.1;
    // this.controlsOrbit.rotateSpeed = is_touch_device() ? -0.5 : -0.3;
    // this.controlsOrbit.enableZoom = true;
    // this.controlsOrbit.enablePan = false;
    // this.controlsOrbit.zoomSpeed = 0.3;
    // this.controlsOrbit.minDistance = 5;
    // this.controlsOrbit.maxDistance = 30;
    // this.controlsOrbit.minZoom = 0.7;
    // this.controlsOrbit.maxZoom = 1.5;
    //
    // //Set Max and Min to verical view
    // this.controlsOrbit.minPolarAngle = (lowerViewAngle * Math.PI) / 180; // Convert degree value to radians
    // this.controlsOrbit.maxPolarAngle = (upperViewAngle * Math.PI) / 180; // Convert degree value to radians

    this.isSpinning = false;
    this.dragging = false;

    this.skipRotate = true;

    this.isMobile = false;

    //Controls for orientation (without touch)
    // IMPORTANT: Initialised in setControls, otherwise iOS error
    // this.deviceControls = new DeviceOrientationControls(this.camera);
    // this.deviceControls.enabled = false;

    //Controls for orientation (with touch)
    this.controlsDevice = new OrbitOrientation(
      this.camera,
      this.renderer.domElement,
    );

    // this.controlsDevice.enabled = false;
    // this.controlsDevice.minZoom = 0.7;
    // this.controlsDevice.maxZoom = 1.5;

    this.controlsDefault();

    //Set the default mode for the viewer
    this.viewerMode = "orbit";
    this._transitioning = false;
    // Create scene manager
    this.sceneManager = new SceneManager(this.renderer, this.camera, this);

    // Add loading manager
    this.loadingManager = new LoadingManager(
      this.container,
      this.stylesheet,
      this.isThumbnail,
    );

    // If textureManager provided use that
    if (this._options["textureManager"]) {
      this.textureManager = this._options.textureManager;
    } else {
      // console.log("USING OLD TEXTURE MANAGER");
      this.textureManager = new TextureManager();
    }

    // Add listeners for mouse and resize
    this.addListeners();

    // Look for intro scenes
    if (this.config.intro !== undefined && !isThumbnail) {
      const { intro } = this.config;
      let scene;

      if (intro.type === "ocean") {
        scene = this.is3d
          ? new SceneOcean(this, intro)
          : new SceneOceanFixed(this, intro);
        this.sceneManager.addScene(scene);
      } else {
        scene = new SceneIntro(this, intro);
        if (!this._options.hasDeckPage) {
          this.sceneManager.addScene(scene);
        }
      }
    } else {
      // If there's images, find which should be the starting image and load it!
      if (Object.keys(config.images).length > 0) {
        let startingImage = config.startingImage;

        if (!startingImage) {
          startingImage = Object.keys(config.images)[0];
        }
        if (!this._options.hasDeckPage) {
          const scene = this.addScene(startingImage);
          if (scene.loadFullRes) {
            scene.loadFullRes();
          }

          let _fov = scene.getSceneFov();
          if (_fov) {
            this.setCameraFov(_fov);
            // eslint-disable-next-line eqeqeq
          } else if (scene.getAspectRatio() != 2) {
            this.setCameraFov(this.PARTIAL_FOV);
          }
        }
      }
    }

    // Start first render!
    this.renderer.setAnimationLoop(this.render);

    if (this.isThumbnail) {
      this.startAutospin();
    }

    this.orientationSupport = false;
    this.orientationSupported = this.orientationSupported.bind(this);
    window.addEventListener("deviceorientation", this.orientationSupported);

    if ("xr" in navigator && !this.isThumbnail) {
      navigator.xr
        .isSessionSupported("immersive-vr")
        .then(available => {
          if (available) {
            this.supportsVR = true;
          }
        })
        .catch(err => {
          console.log("err", err);
        });
    }

    this.onVRSessionStarted = this.onVRSessionStarted.bind(this);
    this.onVRSessionEnded = this.onVRSessionEnded.bind(this);
    // this.toggleFloorplans = this.toggleFloorplans.bind(this);

    // add listener for iframe control
    eventTours.attachPlayerListener(this);

    this.findTooltips();
  }

  setCameraFov(fov) {
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();

    return this.camera;
  }

  controlsDefault() {
    let upperViewAngle = MAX_VERT_VIEW_ANGLE;
    let lowerViewAngle = MIN_VERT_VIEW_ANGLE;
    if (this.config.options) {
      if (this.config.options.vertUpperLim !== undefined) {
        upperViewAngle = this.config.options.vertUpperLim;
      }
      if (this.config.options.vertLowerLim !== undefined) {
        lowerViewAngle = this.config.options.vertLowerLim;
      }
    }

    this.controlsOrbit.mainCamera = this.camera;
    this.controlsOrbit.enableDamping = true;
    this.controlsOrbit.dampingFactor = 0.1;
    this.controlsOrbit.rotateSpeed = is_touch_device() ? -0.5 : -0.3;
    this.controlsOrbit.enableZoom = true;
    this.controlsOrbit.enablePan = false;
    this.controlsOrbit.zoomSpeed = 0.3;
    this.controlsOrbit.minDistance = 5;
    this.controlsOrbit.maxDistance = 30;
    this.controlsOrbit.minZoom = 0.7;
    this.controlsOrbit.maxZoom = 1.5;

    //Set Max and Min to verical view

    this.controlsOrbit.maxPolarAngle = (upperViewAngle * Math.PI) / 180; // Convert degree value to radians
    this.controlsOrbit.minPolarAngle = (lowerViewAngle * Math.PI) / 180; // Convert degree value to radians

    this.controlsOrbit.maxAzimuthAngle = Infinity;
    this.controlsOrbit.minAzimuthAngle = -Infinity;

    this.controlsDevice.enabled = false;
    this.controlsDevice.minZoom = 0.7;
    this.controlsDevice.maxZoom = 1.5;
  }

  // popup override
  popupOverride = false;
  activePopup = null;
  previousPopupCoords = { x: null, y: null };
  centeredPopup = false;
  onPopupOpen(config) { } // this gets overriden by the UI
  onPopupMove({ x, y }) { }

  async forceOpenPopup(scene, popup) {
    if (this.sceneManager.activeScene()?._image?.id !== scene) {
      await this.transitionTo(scene);
      setTimeout(() => this.forceOpenPopup(scene, popup), 100);
    } else {
      const hotspots = this.sceneManager.activeScene()._hotspots;
      const hotspot = hotspots.find(x => x._config.uuid === popup);
      if (!hotspot) {
        console.error("Hotspot not found");
        return;
      }
      this.activePopup = null;
      this.centeredPopup = true;
      this.onPopupOpen(hotspot._config);
      this.onPopupMove({ x: null, y: null });
    }
  }

  openFakePopup(config) {
    this.activePopup = null;
    this.centeredPopup = true;
    this.onPopupOpen(config);
    this.onPopupMove({ x: null, y: null });
  }

  // for handling popups when not in a scene
  // i.e. map intro
  handlePopup(config) {
    if (["link", "elevate-up", "elevate-down", "fade"].includes(config.type)) {
      this.transitionTo(config.options.id);
      return true;
    } else {
      this.openFakePopup(config);
      return false;
    }
  }

  // perma tooltip (gets override by UI)
  tooltips = [];
  setTooltips() { }
  setTooltipLocation = {};
  setIsTransitioning() { }

  clearAllTooltips() {
    for (const id in this.setTooltipLocation) {
      if (this.setTooltipLocation[id]) {
        this.setTooltipLocation[id]({ left: -10000, top: -10000 });
      }
    }
    this.tooltips = [];
    this.setTooltips([]);
  }

  findTooltips(id = null) {
    // find perma tooltips
    // for some reason, the active scene isn't ready when this is called
    // so wait for it if the id doesn't match
    if (
      (this.deviceControls && this.deviceControls.enabled) ||
      this.isSpinning ||
      this._transitioning ||
      (this.sceneManager && this.sceneManager._transitioning)
    ) {
      this.clearAllTooltips();
      return;
    }

    if (!this.sceneManager) return;

    const scene = this.sceneManager.activeScene();
    if (scene) {
      if (scene.split) {
        this.clearAllTooltips();
        return;
      }
      if (id && id !== scene._image.id) {
        setTimeout(() => this.findTooltips(id), 100);
      } else {
        const hotspots = scene._hotspots;
        if (hotspots) {
          this.tooltips = hotspots.filter(
            hotspot => hotspot._config.permaTooltip,
          );
        }
        this.setTooltips(
          this.tooltips.map(x => ({
            id: x._config.uuid,
            title: x._config.tooltip,
          })),
        );
      }
    }
  }

  isSlowConnection() {
    return this.slowConnection;
  }

  clearSlowTimer() {
    if (this.slowConnectionTimer) {
      window.clearTimeout(this.slowConnectionTimer);
      this.slowConnectionTimer = null;
      this.slowConnection = false;
    }
  }

  checkSlowConnection(timeout = 5000) {
    if (this.slowConnectionTimer) {
      clearTimeout(this.slowConnectionTimer);
    }
    this.slowConnectionTimer = window.setTimeout(() => {
      console.warn("[SLOW CONNECTION DETECTED]");
      this.slowConnection = true;
      this.slowConnectionTimer = null;
    }, timeout);
  }

  orientationSupported(event) {
    if (this.orientationSupport === false && event.alpha !== null) {
      this.isMobile = true; // This is a weak assumption
      this.orientationSupport = true;
      this.onDeviceControlsSupported();

      //Analytics
      window.gtag("event", "supports", {
        event_category: "controls",
        event_label: "Orientation Controls",
      });
    }
  }

  // When projecting (i.e in WebXR) the camera is
  // different to the usual one
  getActiveCamera() {
    if (this.renderer && "xr" in this.renderer) {
      return this.renderer.xr.enabled
        ? this.renderer.xr.getCamera(this.camera)
        : this.camera;
    }
    return null;
  }

  startIntro() {
    this.sceneManager.startTransition();

    this.onTransition(undefined);

    setTimeout(() => {
      this.controlsOrbit.reset();
    }, 2000);
  }

  addScene(uuid) {
    const scene = this.getSceneFromUUID(uuid);

    if (scene) {
      this.sceneManager.addScene(scene);
      return scene;
    }
    console.warn("Unable to add scene");
    return undefined;
  }

  getSceneFromUUID(uuid) {
    this.activeScene = uuid;

    const image = this.findImageByUUID(uuid);
    if (image !== null) {
      if (!this.hasPrefetched(uuid)) {
        let timeout;
        if (image.cube ?? false) {
          timeout = 5000 * (this.is3D() ? 2 : 1);
        } else {
          timeout = 3000 * (this.is3D() ? 2 : 1);
        }
        if (this.mode === "editor") {
          timeout *= 3;
        }
        this.checkSlowConnection(timeout);
      }
      return this.sceneFromImage(image);
    }

    return false;
  }

  is3D() {
    return this.config.options && this.config.options.all3D;
  }

  sceneFromImage(image) {
    if (this.is3D()) {
      return new Scene3D(this, image, () => {
        this.clearSlowTimer();
      });
    }
    return new Scene360(this, image, () => {
      this.clearSlowTimer();
    });
  }

  getSplitscreenScene(uuid1, uuid2) {
    const image1 = this.findImageByUUID(uuid1);
    const image2 = this.findImageByUUID(uuid2);

    return new SceneSplit(this, image1, image2, () => {
      this.clearSlowTimer();
    });
  }

  hasPrefetched(uuid) {
    return (this.prefetchedScenes[uuid] ?? 0) > 0;
  }

  prefetchSceneFromUUID(uuid, logName = "", callback = () => { }) {
    return new Promise((resolve, reject) => {
      if (!this.hasPrefetched(uuid)) {
        if (uuid in this.config.images) {
          let image = this.config.images[uuid];

          if ("urls" in image && "full" in image.urls) {
            // let log = "";
            // if (logName) {
            //   log += logName + ' - ';
            // }
            // log += image.urls.full;
            // console.log("PREFETCHING: " + log);
            this.prefetchedScenes[uuid] = 1;

            let stereo = [false];
            if (this.is3D()) {
              stereo = [
                { half: "bottom", isStereo: true },
                { half: "top", isStereo: true },
              ];
            }

            let promises = [];
            for (let i in stereo) {
              if (this.textureManager?.fetch) {
                promises.push(
                  this.textureManager.fetch({
                    image: image,
                    flip: true,
                    nonCritical: false,
                    callback: (texture, full, res) => {
                      callback(full, res);
                      if (full) {
                        this.prefetchedScenes[uuid] = 2;
                      }
                    },
                    stereo: stereo[i],
                    lowRes: false,
                    resolution: "high",
                    isThumbnail: false,
                  }),
                );
              }
            }
            Promise.all(promises).then(() => {
              resolve(false);
            });
          }
        }
      }
      resolve(true);
    });
  }

  findImageByUUID(uuid) {
    if (uuid in this.config.images) {
      return this.config.images[uuid];
    } else {
      console.error("Image not found");
      return null;
    }
  }

  /**
   * Add listeners adds all required event listeners
   */
  addListeners() {
    this._onMouseDown = this.onMouseDown.bind(this);
    this._onMouseUp = this.onMouseUp.bind(this);
    this._onMouseMove = this.onMouseMove.bind(this);
    this._onContextMenu = this.onContextMenu.bind(this);
    this._onTouchpadUp = this.onTouchpadUp.bind(this);
    this._onWindowResize = this.onWindowResize.bind(this);
    this._onMouseOut = this.onMouseOut.bind(this);
    this.container.addEventListener("mousedown", this._onMouseDown, false);
    this.container.addEventListener("touchstart", this._onMouseDown, false);
    this.container.addEventListener("mouseup", this._onMouseUp, false);
    this.container.addEventListener("touchend", this._onMouseUp, false);
    this.container.addEventListener("mouseout", this._onMouseOut, false);
    this.container.addEventListener("mousemove", this._onMouseMove, false);
    this.container.addEventListener("contextmenu", this._onContextMenu, false);
    this.container.addEventListener("gamepadpress", this._onTouchpadUp, false);

    // Resize
    window.addEventListener("resize", this._onWindowResize, false);
  }

  removeListeners() {
    this.container.removeEventListener("mousedown", this._onMouseDown);
    this.container.removeEventListener("touchstart", this._onMouseDown);
    this.container.removeEventListener("mouseup", this._onMouseUp);
    this.container.removeEventListener("touchend", this._onMouseUp);
    this.container.addEventListener("mouseout", this._onMouseOut);
    this.container.removeEventListener("mousemove", this._onMouseMove);
    this.container.removeEventListener("contextmenu", this._onContextMenu);
    this.container.removeEventListener("gamepadpress", this._onTouchpadUp);
    window.removeEventListener(
      "deviceorientation",
      this.orientationSupported,
      false,
    );
    // Resize
    window.removeEventListener("resize", this._onWindowResize);
  }

  /**
   * This is triggered when window is resized.
   * It's used to resize the renderer.
   */
  onWindowResize() {
    /**
     * NOTE: the clientHeight value might include the height of Browser Navigation Bar. 
     * So we need to check: if the player is NOT nested inside another player 
     *   => use window.innerHeight
     */
    const { top, left } = this.container.getBoundingClientRect();
    const isNestedPlayer = top > 0 || left > 0
    const width = isNestedPlayer ? this.container.clientWidth : window.innerWidth;
    const height = isNestedPlayer ? this.container.clientHeight : window.innerHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    if (this.renderer) {
      this.renderer.setSize(width, height);
      this.renderer.domElement.style.width = width + "px";
      this.renderer.domElement.style.height = height + "px";
    }
  }

  handleMouseBorderRotate(rotate) {
    if (this.sceneManager.activeScene() !== undefined) {
      this.sceneManager.activeScene().handleMouseBorderRotate(rotate);
    }
  }

  onMouseMove(event) {
    if (this.sceneManager.activeScene() !== undefined) {
      const mouse = this.mouseFromEvent(event);
      this.sceneManager.activeScene().onMouseMove(event, mouse, this.camera);
    }
  }

  /**
   * call mouseup (aka stop dragging etc) if mouse leaves window
   * @param {MouseEvent} event
   * @returns
   */
  onMouseOut(event) {
    const rect = this.container.getBoundingClientRect();
    const minX = rect.left + this.container.clientLeft;
    const x = event.clientX;
    if (x >= minX || x < minX + this.container.clientWidth) return;
    const minY = rect.top + this.container.clientTop;
    const y = event.clientY;
    if (y >= minY || y < minY + this.container.clientHeight) return;
    this.onMouseUp(event);
  }

  /**
   * Called on mouse up.
   *
   * Handles clicks on the 360 sphere and hotspots
   *
   * @param {MouseEvent} event
   */
  onMouseUp(event) {
    event.preventDefault();
    //Unbind the drag handler
    clearTimeout(this._mouseTimeout);

    this.container.removeEventListener("mousemove", this.dragHandler);
    this.container.removeEventListener("pointermove", this.dragHandler);
    this._mouseTimeout = setTimeout(() => {
      if (this.dragging === true) {
        this.dragging = false;
        this._options.setUIVisibility(true);
        if (this.sceneManager) {
          let activeScene = this.sceneManager.activeScene();
          if (activeScene) {
            activeScene.onMouseStopDrag(event);
          }
        }
      }
    }, 1500);

    //If it is a thumbnail wait then continue to spin
    if (this.isThumbnail) {
      this.stopAutospin();
      setTimeout(() => {
        this.startAutospin();
      }, 800);
    } else {
      // this.stopAutospin();
    }

    if (event.button === 2) {
      return;
    }

    const mouse = this.mouseFromEvent(event);

    if (this.mouseDownPosition) {
      // Check if the user has dragged their mouse
      const xThreshold =
        Math.abs(Math.abs(mouse.x) - Math.abs(this.mouseDownPosition.x)) > 0.01;
      const yThreshold =
        Math.abs(Math.abs(mouse.y) - Math.abs(this.mouseDownPosition.y)) > 0.01;

      // The user dragged their mouse!
      if (xThreshold || yThreshold) {
        return;
      }
    }

    // Check if clicks have been taken over
    if (this.pauseEvents) {
      this.pauseEvents = false;
      this.tempCallback(event);
      return;
    }

    if (this.sceneManager.activeScene() !== undefined) {
      // If this is a right click, handle for editor
      if (event.button !== 2) {
        this.sceneManager.activeScene().onMouseUp(event, mouse, this.camera);
      }
    }
  }

  onTouchpadUp(event) {
    this.stopAutospin();
  }

  async checkPrefetch(timeout = 500) {
    let activeScene = this.sceneManager.activeScene();
    if (activeScene !== undefined) {
      activeScene.onMouseUp(false, false, this.camera);
      if (activeScene.showNodes) {
        await activeScene.checkPrefetch(timeout);
      }
    }
  }

  isVR() {
    return this.viewerMode === "3D";
  }

  /**
   * On context menu is called when the menu should appear
   *
   * @param {MouseEvent} event
   */
  onContextMenu(event) {
    if (this.sceneManager.activeScene() !== undefined) {
      const mouse = this.mouseFromEvent(event);
      const hotspots = this.sceneManager
        .activeScene()
        .hotspotsUnderMouse(mouse, this.camera);
      const videos = this.sceneManager
        .activeScene()
        .pinnedVideosUnderMouse(mouse, this.camera);

      this.onRightClick(event, hotspots, videos);
    }
  }

  /**
   * This logs the mouse position when the mouse is pressed down.
   *
   * It's used to detect if the user dragged their mouse before releasing
   *
   * @param {MouseEvent} event
   */
  onMouseDown(event) {
    // this.onMovement();
    //If floorplans hide them
    // if (this._options.showFloorplans) {
    //   this._options.showFloorplans(false);
    // }

    event.preventDefault();
    this.mouseDownPosition = this.mouseFromEvent(event);

    //Check if right options have been passed
    if (
      this._options.hasOwnProperty("setUIVisibility") &&
      this._options.hasOwnProperty("getUIVisibility") &&
      !this.isThumbnail
    ) {
      //Stop auto play if screen clicked
      // Updated - only show the toolbars
      if (this.isSpinning) {
        //   this.stopAutospin();
        this.showUIWithFadeTimeout()
      }

      //Setup dragging if in correct mode
      if (this.viewerMode === "orbit") {
        if (this.dragHandler === undefined) {
          this.dragHandler = this.onMouseDrag.bind(this);
        }
        clearTimeout(this._mouseTimeout);
        this.container.addEventListener("pointermove", this.dragHandler);
        this.container.addEventListener("ontouchmove", this.dragHandler);
        this.container.addEventListener("mousemove", this.dragHandler);
      }
    }
    if (
      this.sceneManager.activeScene() !== undefined &&
      this.sceneManager.activeScene().onMouseDown
    ) {
      this.sceneManager
        .activeScene()
        .onMouseDown(event, this.mouseDownPosition, this.camera);
    }
  }

  fadeUITimeout = null
  showUIWithFadeTimeout = () => {
    clearTimeout(this.fadeUITimeout)
    this.hideNodes(false);
    this.findTooltips();
    this._options.setUIVisibility(true);
    this.fadeUITimeout = setTimeout(() => {
      if (!this.isSpinning) return
      this.hideNodes(true);
      this.findTooltips();
      this._options.setUIVisibility(false);
    }, 3000)
  }

  onMouseDrag = event => {
    this.onMovement();
    if (!this.sceneManager.activeScene()) {
      return;
    }
    if (
      this.sceneManager !== undefined &&
      this.sceneManager.activeScene() &&
      this.sceneManager.activeScene().SCENE_NAME === "Scene360"
    ) {
      if (this.dragging === false) {
        //Drag Start
        this.dragging = true;
        this._startDragEvent = event;
        return;
      }

      //Calculate distance from start
      const distMoved = Math.sqrt(
        Math.pow(event.clientX - this._startDragEvent.clientX, 2) +
        Math.pow(event.clientY - this._startDragEvent.clientY, 2),
      );

      if (this._options.getUIVisibility() && distMoved > 50) {
        this._options.setUIVisibility(false);
      }

      this.checkPrefetch(500);
    }
    if (this.sceneManager.activeScene().onMouseDrag)
      this.sceneManager.activeScene().onMouseDrag(event);
  };

  onHorizonOffsetUpdate(newDegressOffset) {
    const x = isNaN(newDegressOffset.x ?? 0) ? 0 : newDegressOffset.x;
    const z = isNaN(newDegressOffset.z ?? 0) ? 0 : newDegressOffset.z;
    const scene = this.sceneManager.activeScene();
    scene.updateHorizonOffset({ x, z });
  }

  // fade nodes
  hideNodes(direction) {
    if (!this.sceneManager) return;
    const scene = this.sceneManager.activeScene();
    if (
      scene &&
      (scene.SCENE_NAME === "Scene360" || scene.SCENE_NAME === "SceneSplit")
    ) {
      scene.fadeHotspots(direction ? "OUT" : "IN");
    }
  }

  // instant hide/show nodes
  onSetNodeVisibility(setTo) {
    const scene = this.sceneManager.activeScene();
    if (scene && scene.SCENE_NAME === "Scene360") {
      scene.setNodeVisibility(setTo);
    }
  }

  onClearHotspotHover() {
    const scene = this.sceneManager.activeScene();
    if (scene) scene.onClearHotspotHover?.();
  }

  /*
   * Pauses click and returns promise for next click
   *
   * This can be used to hijack click events for editor
   *
   * @returns {Promise} event on next click
   */
  onOneClick() {
    this.pauseEvents = true;

    return new Promise(resolve => {
      this.tempCallback = resolve;
    });
  }

  addPinnedVideoMarker(uv) {
    const scene = this.sceneManager.activeScene();
    if (scene) {
      scene.addPinnedVideoMarker(uv);
    }
  }

  /**
   * On right click, let the editor know to display a context menu
   *
   * @param {MouseEvent} event
   * @param {BaseHotspot[]} hotspots
   */
  onRightClick(event, hotspots, videos) { }

  /**
   * Called when a transition is started
   *
   * @param {string} uuid
   */
  onTransition(uuid1, uuid2 = null) {
    this.findTooltips(uuid1);
    this.altImage = uuid2;
    this.divider = 50;
    this.transitionFunctions.forEach(({ func }) => func(uuid1, uuid2));
  }

  transitionFunctions = [];

  /**
   * Add a function to be called for each scene transition
   * The function is then called as if set to this.onTransition
   *
   * @param {function} func
   */
  addOnTransitionFunction(func) {
    const id = Math.random();
    this.transitionFunctions.push({ id, func });
    return id;
  }

  removeOnTransitionFunction(id) {
    this.transitionFunctions = this.transitionFunctions.filter(
      ({ id: functionId }) => id !== functionId,
    );
  }

  divider = 50;

  /**
   * Returns a THREE.Vector2 from an event
   * Helper method to get the normalized mouse position
   *
   * @param {MouseEvent} event
   * @returns {THREE.Vector2} position
   */
  mouseFromEvent(event) {
    const mouse = new THREE.Vector2();

    // can also be passed a TouchEvent
    if (event.clientX === undefined) event = event.changedTouches[0];

    mouse.x =
      ((event.clientX - this.container.offsetLeft) /
        this.renderer.domElement.clientWidth) *
      2 -
      1;
    mouse.y =
      -(
        ((event.clientY - this.container.offsetTop) /
          this.renderer.domElement.clientHeight) *
        2
      ) + 1;

    return mouse;
  }

  /**
   * Gets the intersect with the mouse and the photosphere
   *
   * @param {MouseEvent} event
   */
  intersectWithActivePhotoHolder(event) {
    const mouse = this.mouseFromEvent(event);

    return this.sceneManager
      .activeScene()
      .intersectWithPhotoHolder(mouse, this.camera);
  }

  /**
   *
   * @param uuid
   * @param triggeredBy
   * @param delay
   * @param type
   * @returns {Promise<boolean>}
   */
  async transitionToImmediately(uuid, triggeredBy, delay, type) {
    if (!uuid) return;
    if (!this.isThumbnail) {
      if (eventTours.lockControl) return;
      const newArguments = [uuid];
      eventTours.internalEmit(
        eventTours.EVENTS.transitionToImmediately,
        newArguments,
      );
    }
    this.resetControls();
    this.splashscreen = false;
    if (this.sceneManager?.activeScene?.()) {
      this.isInitialTourLoad = false;
    }
    this.previousScene = this.sceneManager?.activeScene()?._image.id ?? null;

    // If already transitioning, then don't transition
    if (this.sceneManager._transitioning || this._transitioning) {
      return;
    }

    this.clearAllTooltips();
    this.setIsTransitioning(true);

    if (!this.isDisposed) this.onStartImageLoad();
    this._transitioning = true;

    this.activeScene = uuid;

    try {
      const scene = this.getSceneFromUUID(uuid);

      if (!scene) {
        throw new Error("Scene not loaded");
      } else if (!scene.loadedPromise) {
        throw new Error("Promise not made yet");
      }

      try {
        await scene.loadedPromise;

        this.sceneManager?.switchScene?.(scene);
        this._transitioning = false;
        this.setIsTransitioning(false);
        scene.loadFullRes?.();
        this.onTransition(uuid);
        if (!this.isDisposed) this.onEndImageLoad?.();
        return true;
      } catch (err) {
        console.warn("Scene not transitioned to", err);
        this._transitioning = false;
        this.setIsTransitioning(false);
        this.captureException(err);
      }
    } catch (err) {
      console.warn(err);
      this.setIsTransitioning(false);
      this.onTransition(uuid);
      this._transitioning = false;
      this.sceneManager &&
        this.sceneManager.activeScene &&
        this.sceneManager.activeScene() &&
        this.sceneManager.activeScene().fadeHotspots("IN");
      this.captureException(err);
    }

    if (!this.isDisposed) this.onEndImageLoad?.();
    return false;
  }

  /**
   *
   * @param uuid
   * @param triggeredBy
   * @param delay
   * @param type
   * @returns {Promise<boolean>}
   */
  async transitionToSplitscreen(uuid1, uuid2) {
    if (!this.isThumbnail) {
      if (eventTours.lockControl) return;
      const newArguments = [uuid1, uuid2];
      eventTours.internalEmit(
        eventTours.EVENTS.transitionToImmediately,
        newArguments,
      );
    }
    this.resetControls();
    this.splashscreen = false;

    // If already transitioning, then don't transition
    if (this.sceneManager._transitioning || this._transitioning) {
      return;
    }

    this.clearAllTooltips();
    this.setIsTransitioning(true);

    if (!this.isDisposed) this.onStartImageLoad();
    this._transitioning = true;

    this.activeScene = uuid1;

    try {
      const scene = this.getSplitscreenScene(uuid1, uuid2);

      if (!scene) {
        throw new Error("Scene not loaded");
      } else if (!scene.loadedPromise) {
        throw new Error("Promise not made yet");
      }

      try {
        await scene.loadedPromise;
        scene.fadeHotspots('OUT');

        this.sceneManager?.switchScene?.(scene);
        this._transitioning = false;
        this.setIsTransitioning(false);

        scene.loadFullRes?.();

        this.onTransition(uuid1, uuid2);
        if (!this.isDisposed) this.onEndImageLoad?.();
        return true;
      } catch (err) {
        console.warn("Scene not transitioned to", err);
        this._transitioning = false;
        this.setIsTransitioning(false);
        this.captureException(err);
      }
    } catch (err) {
      console.warn(err);
      this.setIsTransitioning(false);
      this.onTransition(uuid1, uuid2);
      this._transitioning = false;
      this.sceneManager &&
        this.sceneManager.activeScene &&
        this.sceneManager.activeScene() &&
        this.sceneManager.activeScene().fadeHotspots("IN");
      this.captureException(err);
    }

    if (!this.isDisposed) this.onEndImageLoad?.();
    return false;
  }

  captureException(err) {
    if (window.Sentry) window.Sentry.captureException(err);
  }

  isTransitioning() {
    return this.sceneManager.isTransitioning();
  }

  isSplashScreen() {
    return window.showSplash === true;
  }

  /**
   *
   * @param {String} uuid
   * @param {Object} triggeredBy
   * @param {number} delay             DEPRECATED
   * @param {String} type              "fade" or "no-parallax" or something else
   * @param {Function} onFinishLoading callback function
   * @param {Object} opts              { transitionSpeed: number }
   */
  async transitionTo(uuid, triggeredBy, type, onFinishLoading = () => { }, opts) {
    if (!this.isThumbnail) {
      if (eventTours.lockControl) return;
      const newArguments = [uuid, triggeredBy, type, onFinishLoading];
      eventTours.internalEmit(
        eventTours.EVENTS.transitionTo,
        newArguments, // previously Array.from(arguments) but suddenly stop working
      );
    }
    this.resetControls();
    this.splashscreen = false;
    if (this.sceneManager?.activeScene?.()) {
      this.isInitialTourLoad = false;
    }
    this.previousScene = this.sceneManager?.activeScene()?._image?.id ?? null;

    // If already transitioning, then don't transition
    if (this.sceneManager._transitioning || this._transitioning) {
      return;
    }

    this.clearAllTooltips();
    this.setIsTransitioning(true);

    this.onStartImageLoad();
    this._transitioning = true;

    if (
      this.sceneManager.activeScene() &&
      this.sceneManager.activeScene().fadeHotspots
    ) {
      this.sceneManager.activeScene().fadeHotspots("OUT");
    } else {
      this.activeScene = uuid;

      const scene = this.getSceneFromUUID(uuid);

      if (scene !== null) {
        this.sceneManager?.switchScene?.(scene);
      } else {
        return;
      }
    }

    try {
      const targetPosition = {
        x: 0,
        y: 0,
        z: 0,
      };

      const triggeredAt = {
        x: 0,
        y: 0,
        z: 0,
      };

      if (!!triggeredBy && !!triggeredBy.position) {
        targetPosition.x = -triggeredBy.position.x * 0.6;
        targetPosition.y = -triggeredBy.position.y * 0.6;
        targetPosition.z = -triggeredBy.position.z * 0.6;

        triggeredAt.x = triggeredBy.position.x;
        triggeredAt.y = triggeredBy.position.y;
        triggeredAt.z = triggeredBy.position.z;
      }

      const scene = this.addScene(uuid);
      if (!scene) {
        throw new Error("Scene not loaded");
      } else if (!scene.loadedPromise) {
        throw new Error("Promise not made yet");
      }

      try {
        await scene.loadedPromise;

        setTimeout(onFinishLoading);

        if (type === "fade") {
          this.sceneManager.startFadeTransition(opts?.transitionSpeed);
        } else {
          this.sceneManager.startTransition(
            triggeredAt,
            targetPosition,
            type === "no-parallax" ? false : true,
          );
        }
        this._transitioning = false;
        this.onTransition(uuid);
        if (!this.isDisposed) this.onEndImageLoad?.();
        return true;
      } catch (err) {
        console.warn("Scene not transitioned to", err);
        this._transitioning = false;
        this.captureException(err);
      }
    } catch (err) {
      console.warn(err);
      this.onTransition(uuid);
      this._transitioning = false;
      this.sceneManager.activeScene() &&
        this.sceneManager.activeScene().fadeHotspots("IN");
      this.captureException(err);
    }

    if (!this.isDisposed) this.onEndImageLoad?.();
    return false;
  }

  getCurrentRotation() {
    return this.camera.rotation;
  }

  resetControlsOrbit() {
    this.controlsOrbit.reset();
  }

  getActiveImageObject() {
    if (!this.config) {
      throw new Error("Config not defined");
    }
    if (!("images" in this.config)) {
      throw new Error("Images not in config");
    }
    const image = this.config.images[this.getActiveImage()];
    return image;
  }

  getAltImages(alts = []) {
    return Object.values(this.config.images).filter(img =>
      alts.includes(img.id),
    );
  }

  getTimeline() {
    const image = this.getActiveImageObject();
    if (!image) return [];
    if (Array.isArray(image.alts) && image.alts.length > 0) {
      return [image, ...this.getAltImages(image.alts)];
    }
    const images = this.config?.images ? Object.values(this.config.images) : [];
    const base = images.find(img => Array.isArray(img?.alts) && img.alts.includes(image.id));
    if (base) {
      return [base, ...this.getAltImages(base.alts)];
    }
    return [image];
  }

  getImagesExcluding(uuids) {
    return Object.values(this.config.images).filter(
      img => !uuids.includes(img.id),
    );
  }

  setLoopedAutoplay(value) {
    this.loopedAutoplay = value;
  }

  orbitControlZoomIn(factor) {
    this.controlsOrbit.zoomIn(factor)
  }

  orbitControlZoomOut(factor) {
    this.controlsOrbit.zoomOut(factor)
  }

  startAutospin(images = false, speed = 1.8) {
    if (this.isThumbnail) {
      this.isSpinning = true;
      this._options.onAutoPlayStateChange?.(true);
      this.hideNodes(true);
      this.clearAllTooltips();
      this.controlsOrbit.autoRotate = true;
      this.controlsOrbit.autoRotateSpeed = speed;
      this.controlsOrbit.update();
    } else {
      if (eventTours.lockControl) return;
      const newArguments = [images, speed];
      eventTours.internalEmit(eventTours.EVENTS.startAutospin, newArguments);

      this._options.setUIVisibility(false);

      setTimeout(() => {
        this.isSpinning = true;
        this._options.onAutoPlayStateChange?.(true);
        this.hideNodes(true);
        this.clearAllTooltips();
        this.controlsOrbit.autoRotate = true;
        this.controlsOrbit.autoRotateSpeed = speed;
        this.controlsOrbit.update();

        if (images) {
          this.transitionAutospin(images, 0, speed);
        }
      }, 250);
    }
    setTimeout(() => {
      this.isSpinning = true;
      this._options.onAutoPlayStateChange?.(true);
      this.hideNodes(true);
      this.clearAllTooltips();
      this.controlsOrbit.autoRotate = true;
      this.controlsOrbit.autoRotateSpeed = speed;
      this.controlsOrbit.update();

      if (images) {
        this.transitionAutospin(images, 0, speed);
      }
    }, 250);
  }

  stopAutospin() {
    clearTimeout(this.autoplayTransitionTimeout);
    clearInterval(this.autoplayCheckInterval);
    clearTimeout(this.fadeUITimeout);
    this.stopAutoplayProgressTracking();
    if (!this.isThumbnail) {
      if (eventTours.lockControl) return;
      const newArguments = [];
      eventTours.internalEmit(eventTours.EVENTS.stopAutospin, newArguments);
    }
    if (this.isSpinning) {
      if (!this.isThumbnail) {
        this._options.setUIVisibility(true);
      }
      this.isSpinning = false;
      this._options.onAutoPlayStateChange?.(false);
      this._options.resetProgressBar?.(false);
      this.hideNodes(false);
      this.findTooltips();
      this.controlsOrbit.autoRotate = false;
      this.controlsOrbit.update();
    }
  }

  startAutoplayProgressTracking(duration) {
    this.stopAutoplayProgressTracking();
    this.autoplayProgressDuration = duration;
    this.autoplayProgressStartTime = Date.now();
    
    // Update progress every 50ms for smooth animation
    this.autoplayProgressInterval = setInterval(() => {
      if (!this.isSpinning || !this.autoplayProgressStartTime) {
        this.stopAutoplayProgressTracking();
        return;
      }
      
      const elapsed = Date.now() - this.autoplayProgressStartTime;
      const progressPercent = Math.min(Math.max((elapsed / this.autoplayProgressDuration) * 100, 0), 100);
      this._options.onAutoplayProgressUpdate?.(progressPercent);
    }, 50);
    
    this._options.onAutoplayProgressUpdate?.(0);
  }

  stopAutoplayProgressTracking() {
    if (this.autoplayProgressInterval) {
      clearInterval(this.autoplayProgressInterval);
      this.autoplayProgressInterval = null;
    }
    this.autoplayProgressStartTime = null;
    this.autoplayProgressDuration = null;
    this._options.onAutoplayProgressUpdate?.(0);
  }

  resetFlows() {
    this.autoplayFlows = [];
  }

  setSkipRotate(val) {
    this.skipRotate = val;
  }

  async waitForScene(imageId, timeout = 3000) {
    await new Promise(resolve => {
      if (this.sceneManager.activeScene()?._image?.id === imageId) {
        resolve();
      } else {
        const checkSceneInterval = setInterval(() => {
          if (this.sceneManager.activeScene()?._image?.id === imageId) {
            clearInterval(checkSceneInterval);
            resolve();
          }
        }, 100)
        setTimeout(() => {
          clearInterval(checkSceneInterval);
          resolve();
        }, timeout)
      }
    })
  }

  transitionAutospin(images, i, speed) {
    if (this.autoplayTransitionTimeout) {
      clearTimeout(this.autoplayTransitionTimeout);
    }
    if (this.autoplayCheckInterval) {
      clearInterval(this.autoplayCheckInterval);
    }
    const delayTime = 9500 * (1.8 / speed); // slower speed takes longer time to complete round
    
    // Start progress tracking
    this.startAutoplayProgressTracking(delayTime);
    
    this.autoplayTransitionTimeout = setTimeout(() => {
      this.stopAutoplayProgressTracking();
      if (this.isSpinning) {
        if (i < images.length) {
          this.transitionTo(images[i], undefined, "fade");

          this.transitionAutospin(images, i + 1, speed);
        } else {
          if (!this.loopedAutoplay) {
            this.stopAutospin();
            this.resetControlsOrbit();
            this._options.onCompletedAutoplay?.();
            return;
          }
          this.transitionTo(images[0]);

          this.transitionAutospin(images, 1, speed);
        }
      }
    }, delayTime);
  }

  activeFlow = null;
  currentStepIdx = null;
  autoplayFlows = [];
  async startFlow(flow, speed) {
    this.activeFlow = flow;
    this.currentStepIdx = null;
    if (
      this.getActiveImage() !== flow.start ||
      flow.steps.map(x => x.image).includes(this.getActiveImage())
    ) {
      await this.transitionTo(flow.start, undefined, "fade");
      // Wait for POI hotspot to load on the first image
      await new Promise(resolve => setTimeout(resolve, 500));
      const activeScene = this.sceneManager.activeScene();
      if (activeScene._hotspots?.some(i => i._config?.type === "poi")) {
        await this.waitForPOIHotspot();
      }
    }

    this.isSpinning = true;
    await this.waitForScene(flow.start);
    if (this.skipRotate) {
      this.transitionFlowSkipRotate(speed)
    } else {
      this.transitionFlow(speed);
    }
  }

  getCurrentStepIdx() {
    const flow = this.activeFlow;
    if (!flow) return;
    const steps = flow.steps || [];
    if (!steps || !steps.length) return;
    const _activeImage = this.getActiveImage();
    if (_activeImage === flow.start && this.currentStepIdx === null) {
      return -1;
    }
    if (this.currentStepIdx !== null) {
      if (_activeImage && steps?.[this.currentStepIdx]?.image === _activeImage) {
        return this.currentStepIdx;
      }
    }
    return steps.findIndex(x => x.image === _activeImage);
  }

  async continueFlow(speed) {
    this.isSpinning = true;
    if (this.skipRotate) {
      this.transitionFlowSkipRotate(speed)
    } else {
      this.transitionFlow(speed);
    }
  }

  async startMultiFlows(flows, speed) {
    const newArguments = [flows, speed];
    eventTours.internalEmit(eventTours.EVENTS.startMultiFlows, newArguments);
    if (this.autoplayFlows
      && this.autoplayFlows.length === flows.length
      && !!this.activeFlow
    ) {
      this.continueFlow(speed)
      return
    }
    // Sort flows to prioritize flow containing active image
    const activeImage = this.getActiveImage();
    let sortedFlows = [...flows]
    let firstFlowIdx = sortedFlows.findIndex(f => f.start === activeImage)
    if (firstFlowIdx > 0) {
      const firstFlow = sortedFlows[firstFlowIdx]
      sortedFlows.splice(firstFlowIdx, 1)
      sortedFlows.unshift(firstFlow)
    }
    this.autoplayFlows = sortedFlows
    this.startFlow(sortedFlows[0], speed)
  }

  async continueMultiFlows(flow, speed) {
    let nextFlowIdx = this.autoplayFlows.findIndex(f => f.id === flow.id)
    if (nextFlowIdx === -1) nextFlowIdx = 0
    nextFlowIdx++
    if (nextFlowIdx >= this.autoplayFlows.length) nextFlowIdx = 0
    this.startFlow(this.autoplayFlows[nextFlowIdx], speed)
  }

  async startFreeRotate(speedScale, estimatedDuration) {
    const previousAutoRotate = this.controlsOrbit.autoRotate;
    const previousAutoRotateSpeed = this.controlsOrbit.autoRotateSpeed;

    this.controlsOrbit.autoRotate = true;
    this.controlsOrbit.autoRotateSpeed = speedScale * 0.5;
    this.controlsOrbit.update();

    await new Promise(resolve => setTimeout(resolve, estimatedDuration));

    if (!this.isSpinning) {
      this.controlsOrbit.autoRotate = previousAutoRotate;
      this.controlsOrbit.autoRotateSpeed = previousAutoRotateSpeed;
      this.controlsOrbit.update();
      this.stopAutoplayProgressTracking();
      return;
    }

    this.controlsOrbit.autoRotate = previousAutoRotate;
    this.controlsOrbit.autoRotateSpeed = previousAutoRotateSpeed;
    this.controlsOrbit.update();
  }

  transitionFlow = async (speed = 1.8) => {
    if (!this.isSpinning) return;
    const delayTime = 9500 * (1.8 / speed);
    const flow = this.activeFlow;
    if (!flow) return;

    this._options.onAutoPlayStateChange?.(true);
    this.hideNodes(true);
    this.clearAllTooltips();

    this._options.setUIVisibility(false);
    this.controlsOrbit.autoRotate = true;
    this.controlsOrbit.autoRotateSpeed = speed;
    this.controlsOrbit.update();
    const currentStepIndex = this.getCurrentStepIdx();

    let nextStepIdx = currentStepIndex + 1;
    if (nextStepIdx >= flow.steps?.length && this.autoplayFlows.length) {
      this.continueMultiFlows(flow, speed);
      return;
    }
    if (nextStepIdx >= flow.steps?.length) { // && this.loopedAutoplay
      nextStepIdx = -1;
    }
    this.currentStepIdx = nextStepIdx;
    const nextStep = nextStepIdx > -1
      ? flow.steps[nextStepIdx]
      : { image: flow.start };

    if (this.autoplayTransitionTimeout) {
      clearTimeout(this.autoplayTransitionTimeout);
    }
    
    // Start progress tracking for this scene
    this.startAutoplayProgressTracking(delayTime);
    
    this.autoplayTransitionTimeout = setTimeout(async () => {
      this.stopAutoplayProgressTracking();
      // this.isSpinning = false;
      // this._options.onAutoPlayStateChange?.(false);
      this.hideNodes(false);
      this.findTooltips();
      this.controlsOrbit.autoRotate = false;
      this.controlsOrbit.update();

      if (!nextStep) {
        this._options.resetProgressBar?.(false);
        return;
      }

      const scene = this.sceneManager.activeScene();
      const hotspots = scene._hotspots;

      const movementNode = hotspots.find(
        h => h._config.options.id === nextStep.image,
      );

      if (movementNode) {
        await movementNode.lookAtMe();
        if (!this.isSpinning) return;
        await movementNode.action();
      } else {
        console.error("No hotspot found for looking at");
        await this.startFreeRotate(speed * 0.5, delayTime);
        await this.transitionTo(nextStep.image, undefined, "fade");
      }

      if (nextStep) {
        setTimeout(() => {
          this.transitionFlow(speed);
        }, 1000);
      }
    }, delayTime);
  };

  // transitionFlowSkipRotate = async (speed = 1.8) => {
  //   if (!this.isSpinning) return;
  //   const delayTime = 9500 * (1.8 / speed);
  //   let startTime = Date.now()
  //   const flow = this.activeFlow;
  //   if (!flow) return;

  //   this._options.onAutoPlayStateChange?.(true);
  //   this.hideNodes(true);
  //   this.setTooltips([]);

  //   this._options.setUIVisibility(false);
  //   this.controlsOrbit.autoRotate = true;
  //   this.controlsOrbit.autoRotateSpeed = speed;
  //   this.controlsOrbit.update();

  //   let currentStepIndex = flow.steps.findIndex(
  //     x => x.image === this.getActiveImage(),
  //   );

  //   let nextStepIdx = currentStepIndex + 1;
  //   if (nextStepIdx >= flow.steps?.length) { // && this.loopedAutoplay
  //     nextStepIdx = 0;
  //   }
  //   if (this.findNextNodeTimeout) {
  //     clearTimeout(this.findNextNodeTimeout)
  //   }
  //   this.findNextNodeTimeout = setTimeout(() => {
  //     this.hideNodes(false);
  //     this.findTooltips();
  //     const nextStep = flow.steps[nextStepIdx];
  //     if (!nextStep) {
  //       if (this.autoplayTransitionTimeout) {
  //         clearTimeout(this.autoplayTransitionTimeout);
  //       }
  //       this.autoplayTransitionTimeout = setTimeout(() => {
  //         this.controlsOrbit.autoRotate = false;
  //         this.controlsOrbit.update();
  //       }, delayTime)
  //       return;
  //     }
  //     const scene = this.sceneManager.activeScene();
  //     const hotspots = scene._hotspots;
  //     const movementNode = hotspots.find(
  //       h => h._config.options.id === nextStep.image,
  //     );
  //     if (!movementNode) {
  //       this.transitionTo(nextStep.image, undefined, "fade");
  //       return;
  //     } else {
  //       // Get target angle
  //       const { x, z } = movementNode.position;
  //       let targetAngle = Math.atan2(z, x);
  //       targetAngle = Math.PI - targetAngle;
  //       if (targetAngle < -Math.PI) {
  //         targetAngle += 2 * Math.PI;
  //       } else if (targetAngle > Math.PI) {
  //         targetAngle -= 2 * Math.PI;
  //       }

  //       // Check angle until we reach target
  //       this.autoplayCheckInterval = setInterval(async () => {
  //         const runTime = Date.now() - startTime;
  //         if (runTime > 600 && !this.checkNeedsToTurn(targetAngle)) {
  //           clearInterval(this.autoplayCheckInterval);
  //           this.controlsOrbit.autoRotate = false;
  //           this.controlsOrbit.update();
  //           // Execute node action after reaching position
  //           try {
  //             await movementNode.action();
  //           } catch (error) {
  //             console.error("Error executing node action", error);
  //           }
  //           setTimeout(() => {
  //             this.transitionFlowSkipRotate(speed);
  //           }, 100);
  //         }
  //       }, 100);
  //     }
  //   }, 800)
  // };

  transitionFlowSkipRotate = async (speed = 3) => {
    if (!this.isSpinning) return;
    const speedScale = 1.8 / speed;
    const flow = this.activeFlow;
    if (!flow) return;
    this._options.onAutoPlayStateChange?.(true);
    this.hideNodes(true);
    this.clearAllTooltips();
    this._options.setUIVisibility(false);
    const currentStepIndex = this.getCurrentStepIdx();

    let nextStepIdx = currentStepIndex + 1;
    if (nextStepIdx >= flow.steps?.length && this.autoplayFlows.length) {
      this.startAutoplayProgressTracking(1000);
      await this.startFreeRotate(speed * 0.5, 1000);
      this.continueMultiFlows(flow, speed);
      return;
    }
    if (nextStepIdx >= flow.steps?.length) { //  && this.loopedAutoplay
      nextStepIdx = -1;
    }
    this.currentStepIdx = nextStepIdx;
    const nextStep = nextStepIdx > -1
      ? flow.steps[nextStepIdx]
      : { image: flow.start };

    this.hideNodes(false);
    this.findTooltips();

    if (!nextStep) {
      this._options.resetProgressBar?.(false);
      return;
    }

    const scene = this.sceneManager.activeScene();
    const hotspots = scene._hotspots;

    const movementNode = hotspots.find(
      h => h._config.options.id === nextStep.image,
    );

    const estimatedDuration = 1600 + 1000;
    this.startAutoplayProgressTracking(estimatedDuration);
    
    if (movementNode) {
      const lookAtMinTime = 1600;
      const startTime = Date.now();
      await movementNode.lookAtMe("", { speedScale: speedScale * 1.6 });
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < lookAtMinTime) {
        await new Promise(resolve => setTimeout(resolve, lookAtMinTime - elapsedTime));
      }
      if (!this.isSpinning) {
        this.stopAutoplayProgressTracking();
        return;
      }
      await movementNode.action();
    } else {
      await this.startFreeRotate(speed * 0.5, estimatedDuration);
      await this.transitionTo(nextStep.image, undefined, "fade");
    }
    
    await this.waitForScene(nextStep.image);
    // Stop progress tracking before the delay, then it will restart in the next call
    this.stopAutoplayProgressTracking();
    setTimeout(() => {
      this.transitionFlowSkipRotate(speed);
    }, 1000);
  };

  /**
   * Return list of images with names
   */
  getImageList() {
    const images = [];

    for (let i in this.config.images) {
      const url = this.config.images[i].urls.low;
      const newUrl = new URL(url);
      newUrl.searchParams.set("q", "30");
      newUrl.searchParams.set("h", this.config.options.all3D ? "200" : "100");
      images.push({
        id: this.config.images[i].id,
        name: this.config.images[i].name,
        image: newUrl.href,
      });
    }

    return images;
  }

  /**
   * Return image from id
   */
  getImage(id) {
    if (id === undefined || id === null) return;
    for (let i in this.config.images) {
      if (this.config.images[i].id.toString?.() === id.toString?.()) {
        return this.config.images[i];
      }
    }
  }

  /**
   * Returns list of floor plans
   *
   * @returns {Object[]} floorplans
   */
  getFloorPlans() {
    if (this.config.floorplans) {
      return this.config.floorplans;
    }

    return [];
  }

  getActiveImage() {
    return this.activeScene ? this.activeScene : this.activeImage;
  }

  toggleControls() {
    switch (this.viewerMode) {
      case "orbit":
        //Set mode to device
        this.setControls("device");
        break;
      case "device":
        //Set mode to stereo
        this.setControls("stereo");
        break;
      case "stereo":
        //Set mode to orbit
        this.setControls("orbit");
        break;
    }
  }

  resetControls(controlsChange = false) {
    this.isPivoting = false;
    this.setControls(this.viewerMode, controlsChange);
  }

  setControls(type, controlsChange = true) {
    window.gtag = () => { };
    switch (type) {
      case "device":
        //Set mode to device
        this.controlsOrbit.enabled = false;
        this.controlsDevice.enabled = true;
        if (this.deviceControls) this.deviceControls.enabled = false;

        this.controlsOrbit.minPolarAngle = (0 * Math.PI) / 180; // Convert degree value to radians
        this.controlsOrbit.maxPolarAngle = (180 * Math.PI) / 180; // Convert degree value to radians
        this.onWindowResize();
        this.viewerMode = "device";
        window.gtag("event", "Device Mode Change", {
          event_category: "Set device mode",
          event_label: `device VR`,
        });
        break;
      case "stereo":
        if (this.deviceControls === undefined) {
          this.deviceControls = new DeviceOrientationControls(this.camera);
          this.deviceControls.enabled = false;
        }
        //Set mode to stereo
        this.controlsOrbit.enabled = false;
        this.controlsDevice.enabled = false;
        this.deviceControls.enabled = true;

        this.controlsOrbit.minPolarAngle = (0 * Math.PI) / 180; // Convert degree value to radians
        this.controlsOrbit.maxPolarAngle = (180 * Math.PI) / 180; // Convert degree value to radians
        this.onWindowResize();
        this.viewerMode = "stereo";
        window.gtag("event", "Device Mode Change", {
          event_category: "Set device mode",
          event_label: `stereo`,
        });
        break;
      case "orbit":
        let upperViewAngle = MAX_VERT_VIEW_ANGLE;
        let lowerViewAngle = MIN_VERT_VIEW_ANGLE;
        if (this.config.options) {
          if (this.config.options.vertUpperLim !== undefined) {
            upperViewAngle = this.config.options.vertUpperLim;
          }
          if (this.config.options.vertLowerLim !== undefined) {
            lowerViewAngle = this.config.options.vertLowerLim;
          }
        }
        //Set mode to orbit
        this.controlsOrbit.enabled = true;
        this.controlsDevice.enabled = false;
        if (this.deviceControls) this.deviceControls.enabled = false;

        this.controlsOrbit.minPolarAngle = (lowerViewAngle * Math.PI) / 180; // Convert degree value to radians
        this.controlsOrbit.maxPolarAngle = (upperViewAngle * Math.PI) / 180; // Convert degree value to radians
        this.onWindowResize();
        this.viewerMode = "orbit";
        window.gtag("event", "Device Mode Change", {
          event_category: "Set device mode",
          event_label: `touch`,
        });
        break;
    }

    if (controlsChange) {
      if (
        this.sceneManager.activeScene() &&
        this.sceneManager.activeScene().onControlsChange
      ) {
        this.sceneManager.activeScene().onControlsChange(type);
      }
      this.onControlsChange(type);
    }

    // hide tooltips if stereo
    this.findTooltips();
  }

  checkHoverHotspot(event) {
    if (this.sceneManager.activeScene() !== undefined) {
      const mouse = this.mouseFromEvent(event);
      return this.sceneManager
        .activeScene()
        .checkHoverHotspot(event, mouse, this.camera);
    }
    return false;
  }

  dispose() {
    this.isDisposed = true;
    this.sceneManager.dispose();
    this.sceneManager = undefined;
    this.textureManager = undefined;
    this.stylesheet = undefined;

    //Stop the animation loop
    this.renderer.setAnimationLoop(null);
    this.renderer = undefined;

    this.render = null;
    this.removeListeners();
    //this.container.remove()
  }

  withinViewport(obj, updateMatrices = false) {
    var camera = this.getActiveCamera();

    if (updateMatrices) {
      camera.updateMatrix();
      camera.updateMatrixWorld();
      obj.updateMatrix();
      obj.updateMatrixWorld();
    }

    var frustum = new THREE.Frustum();
    frustum.setFromProjectionMatrix(
      new THREE.Matrix4().multiplyMatrices(
        camera.projectionMatrix,
        camera.matrixWorldInverse,
      ),
    );

    return frustum.intersectsObject(obj);
  }

  onClickVR() {
    if (this.VRSession === null) {
      this.renderer.setScissorTest(false);
      navigator.xr
        .requestSession("immersive-vr", {
          optionalFeatures: ["local-floor", "bounded-floor", "hand-tracking"],
        })
        .then(this.onVRSessionStarted);
    } else {
      this.VRSession.end();
      this.renderer.setScissorTest(true);
    }
  }

  onVRSessionStarted(session) {
    session.addEventListener("end", this.onVRSessionEnded);
    this.renderer.xr.setSession(session);
    this.VRSession = session;
    this.sceneManager.activeScene().enterVR();
  }

  onVRSessionEnded() {
    this.renderer.setScissorTest(true);
    if (this.VRSession !== null) {
      this.VRSession.removeEventListener("end", this.onVRSessionEnded);
      this.VRSession = null;
    }
    this.sceneManager.activeScene().exitVR();
  }

  // override me
  setLookingAt(name, showEyeIcon) { }

  getHotspotsFromImage(id) {
    const image = this.getImage(id);
    return image.hotspots;
  }

  checkNeedsToTurn(angle) {
    return this.controlsOrbit.checkNeedToTurn(angle);
  }

  async setCameraAngle(name, angle, opts) {
    return new Promise(async (resolve, reject) => {
      if (!this.controlsOrbit?.enabled || this.isSplashScreen()) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      if (this.controlsOrbit.enabled && !this.isSplashScreen()) {
        if (this.controlsOrbit.checkNeedToTurn(angle)) {
          let lookAtTimeout;
          const onCompleteLookAt = () => {
            if (lookAtTimeout) clearTimeout(lookAtTimeout);
            this.setLookingAt(null);
            resolve();
          }
          this.setLookingAt(name, opts?.showEyeIcon);
          this.controlsOrbit.lookAtAngle(angle, onCompleteLookAt, opts);
          lookAtTimeout = setTimeout(() => {
            onCompleteLookAt();
          }, 20000);
        } else {
          resolve();
        }
      } else {
        resolve();
      }
    });
  }

  movementTimeout;

  onMovement() {
    if (
      this.sceneManager !== undefined &&
      this.sceneManager.activeScene() &&
      this.sceneManager.activeScene().SCENE_NAME === "SceneSplit"
    ) {
      clearTimeout(this.movementTimeout);
      this._options.setUIVisibility(false);
      this.hideNodes(true);
      this.movementTimeout = setTimeout(() => {
        this._options.setUIVisibility(true);
        this.hideNodes(false);
      }, 4000);
    }
  }

  /**
   * This is run on each frame and runs the updates
   */
  render = () => {
    // WARNING: HACKY
    // Please find the actual problem and fix it
    if (this.renderer.domElement.clientHeight === 0) this._onWindowResize();

    this.controlsOrbit.update();
    this.controlsDevice.update();
    if (this.deviceControls) this.deviceControls.update();

    const position = new THREE.Vector3();
    this.camera.updateMatrixWorld();
    this.camera.updateProjectionMatrix();

    // Send popup position to UI layer
    if (this.popupOverride && this.activePopup) {
      position.setFromMatrixPosition(this.activePopup.matrixWorld);
      position.project(this.camera);
      // prevent popup appearing 180deg off
      if (position.z <= 1) {
        const widthHalf = window.innerWidth / 2;
        const heightHalf = window.innerHeight / 2;
        const coordinates = {
          x: Math.round(position.x * widthHalf + widthHalf),
          y: Math.round(-(position.y * heightHalf) + heightHalf),
        };
        if (
          coordinates.x !== this.previousPopupCoords.x ||
          coordinates.y !== this.previousPopupCoords.y
        ) {
          this.previousPopupCoords = coordinates;
          this.onPopupMove(coordinates);
        }
      }
    }

    // send tooltips to UI
    for (const hotspot of this.tooltips) {
      const id = hotspot._config.uuid;
      if (this.setTooltipLocation[id]) {
        position.setFromMatrixPosition(hotspot.matrixWorld);
        position.project(this.camera);
        // prevent popup appearing 180deg off
        if (position.z <= 1) {
          const widthHalf = window.innerWidth / 2;
          const heightHalf = window.innerHeight / 2;
          const coordinates = {
            left: position.x * widthHalf + widthHalf,
            top: -(position.y * heightHalf) + heightHalf,
          };
          this.setTooltipLocation[id](coordinates);
        } else {
          this.setTooltipLocation[id]({ left: -1000, top: -1000 });
        }
      }
    }

    if (this.sceneManager.activeScene() !== undefined) {
      this.sceneManager.render(this.renderer, this.camera);
    }
  };

  onControlsChange(controls) { }
  onDeviceControlsSupported() { }

  setLoadingPOIHotspot(value) {
    this.isLoadingPOIHotspot = value;
  }
  waitForPOIHotspot = async () => {
    if (!this.isLoadingPOIHotspot) return;
    let failsafeTimeout;
    return new Promise(resolve => {
      const checkLoading = () => {
        if (!this.isLoadingPOIHotspot) {
          clearTimeout(failsafeTimeout);
          resolve();
        } else {
          setTimeout(checkLoading, 100);
        }
      };
      checkLoading();
      failsafeTimeout = setTimeout(resolve, 5000);
    });
  }

  isShowingPosterPage = false;
  setShowingPosterPage(value) {
    console.log("setShowingPosterPage", value);
    this.isShowingPosterPage = value;
  }
  waitForPosterPageDisappear = async () => {
    return new Promise(resolve => {
      const checkShowing = () => {
        if (!this.isShowingPosterPage) {
          resolve();
        } else {
          setTimeout(checkShowing, 100);
        }
      };
      checkShowing();
    });
  }
}
